interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const deferredPrompt = ref<BeforeInstallPromptEvent | null>(null);
const canInstall = ref(false);
const isIos = ref(false);
const isInstalled = ref(false);
const showIosInstallGuide = ref(false);

export function usePwaInstall() {
  const install = async () => {
    if (!deferredPrompt.value) return false;

    deferredPrompt.value.prompt();
    const { outcome } = await deferredPrompt.value.userChoice;

    deferredPrompt.value = null;
    canInstall.value = false;

    return outcome === 'accepted';
  };

  const checkInstallState = () => {
    if (import.meta.server) return;

    // Don't show install prompts inside native Capacitor app
    if ((window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor?.isNativePlatform?.()) return;

    // Check if running on iOS
    isIos.value = /iPhone|iPad|iPod/i.test(navigator.userAgent) || (/Macintosh/i.test(navigator.userAgent) && navigator.maxTouchPoints > 1);

    // Check if already installed (standalone mode)
    isInstalled.value =
      window.matchMedia('(display-mode: standalone)').matches || (window.navigator as unknown as { standalone?: boolean }).standalone === true;

    // Show iOS install guide if on iOS, not installed, and no native prompt available
    showIosInstallGuide.value = isIos.value && !isInstalled.value && !deferredPrompt.value;
  };

  const initInstallPrompt = () => {
    checkInstallState();

    window.addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault();
      deferredPrompt.value = e as BeforeInstallPromptEvent;
      // canInstall should only be true if the device is a mobile device
      canInstall.value = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      // Recheck iOS guide state after prompt event
      showIosInstallGuide.value = false;
    });

    window.addEventListener('appinstalled', () => {
      deferredPrompt.value = null;
      canInstall.value = false;
      isInstalled.value = true;
      showIosInstallGuide.value = false;
    });

    // Listen for display mode changes (user might install while app is open)
    window.matchMedia('(display-mode: standalone)').addEventListener('change', (e) => {
      isInstalled.value = e.matches;
      if (e.matches) {
        showIosInstallGuide.value = false;
      }
    });
  };

  return {
    canInstall: readonly(canInstall),
    isIos: readonly(isIos),
    isInstalled: readonly(isInstalled),
    showIosInstallGuide: readonly(showIosInstallGuide),
    install,
    initInstallPrompt,
  };
}
